/**
 * LA FICHE CLIENT — les onglets de la CAISSE, dans le MÊME ORDRE (décision utilisateur 2026-08-30) :
 * **Général · Wallet · Historique · Avoirs · Statistiques**, plus **RGPD** propre au back-office
 * (anonymiser et exporter les données d'une personne sont des actes d'administration, pas des gestes
 * de comptoir — la caisse n'a pas cet onglet et ne doit pas l'avoir).
 *
 * ⚠️ MÊMES DONNÉES QUE LA CAISSE : les deux fiches lisent le tronc partagé et les mêmes dérivations
 *    (chiffre et visites = la règle SQL partagée `db-schema/sales-revenue`, cagnotte = grand livre
 *    `loyalty_ledger`) — elles doivent dire la même chose du même client.
 * ⚠️ PLUS D'ONGLET « FIDÉLITÉ » (retiré 2026-08-30, retour utilisateur) : le programme de fidélité
 *    est un réglage GLOBAL du magasin, pas une propriété du client. Ce qui appartient vraiment à la
 *    personne — sa cagnotte et son grand livre — se lit là où la caisse le met : au CLIC sur le
 *    montant des gains (onglet Général) et par « Gains accumulés » (onglet Historique).
 * ⚠️ Écarts assumés avec le dessin BO (LISEZ-MOI n°13) : consentements en opt-in positif, Mobile ET
 *    Fixe, carte générée à l'enregistrement.
 */
import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, confirm, Icon, showToast, Switch, Tooltip, Trans, useLatest, type IconName } from '@orgazone/ui-web';
import { ApiError } from '@orgazone/api-envelope/client';
import { CIVILITY_CODES, clientSinceYear, isCivilityCode, parseBirthdate, type CivilityCode } from '@orgazone/core-crm';
import { switchOn } from '@orgazone/sync-contract';
import { currentCurrency, formatMoney, toInputValue } from '@orgazone/money-format';
import { api, type ClientDetail, type ClientFamilyShare, type ClientInput, type ClientQualification } from '../api';
import { exporterCsv } from './export-csv';
// ⚠️ La palette des bons vient du module PARTAGÉ : la fiche en tenait une SECONDE, qui peignait les
//    mêmes types d'autres couleurs que le registre « Titres & bons » (relevé 2026-09-06).
import { outstandingVoucherCents } from '@orgazone/core-vouchers';
import { formatRateBp, parseRateBp } from '@orgazone/core-vat';
import { bdL, ETAT_TITRE, ledgerTotalView, merchantBalanceView, palierPastille, pendingPassNote, typeUi, walletBadgeOn } from './clients-kit';
import { CommuneField } from './commune-field';
import { divRoundHalfUp, pctBp, rescale } from '@orgazone/core-money';
import { fieldLabel } from '../vocabulary';
import { key, t, type Translatable } from '../i18n';
import { currentLang } from '../lang';

const MONO = 'var(--font-mono)';

/** Les deux morceaux d'une tuile d'historique : le chiffre, puis son unité posée sur la même ligne de base. */
const TILE_FIGURE = (x: string) => <span style={{ fontSize: '20px', fontWeight: 800, color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>{x}</span>;
const TILE_UNIT = (x: string) => <span style={{ fontSize: '12px', color: 'var(--ink-500)' }}>{x}</span>;

/**
 * LE LIBELLÉ DE CHAQUE CIVILITÉ — traduit, alors que la VALEUR enregistrée reste le code.
 * ⚠️ FERMÉ PAR LE TYPE : un code ajouté à `core-crm` sans son libellé ne compile pas.
 * ⚠️ EN TOUTES LETTRES : « M. » seul n'est pas un texte que l'on traduit, « Monsieur » l'est — et se
 *    traduit court (« Mr », « Sr. »).
 * ⚠️ La traduction n'atteint jamais la base : enregistrer « Ms » sous un compte anglais écrivait une
 *    valeur qu'aucune caisse ni aucune campagne ne reconnaissait.
 */
const CIVILITE_LIBELLES: Record<CivilityCode, Translatable> = { 'M.': key('Monsieur'), Mme: key('Madame') };

/** Le gabarit d'une pastille de qualification — capsule claire, bord fin, point coloré à gauche. */
const pastilleQualif = (couleur: string | null): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', gap: 8, height: 36, padding: '0 14px',
  border: `1px solid ${couleur ? `color-mix(in oklab, ${couleur} 35%, var(--ink-200))` : 'var(--ink-200)'}`,
  borderRadius: 'var(--r-pill)', background: 'var(--paper)',
  color: 'var(--ink-800)', fontSize: '13.5px', fontWeight: 600,
});

/** Le nom d'une part du panier — la famille du magasin, ou la sorte que le serveur a désignée (jamais un texte envoyé). */
function familyName(fam: ClientFamilyShare): string {
  if (fam.kind === 'unset') return t('Sans famille');
  if (fam.kind === 'others') return t('Autres');
  return fam.family;
}

/**
 * Le POINT d'une qualification. ⚠️ Sans couleur réglée au référentiel, un ton NEUTRE : inventer une
 * couleur la ferait passer pour un choix du magasin, et deux écrans en choisiraient deux (§23.0).
 */
function PointQualif({ color }: { color: string | null }) {
  return <span aria-hidden style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: color ?? 'var(--ink-300)' }} />;
}
const euros = (cents: number): string => formatMoney(cents, { thousandsSep: ' ' });

/** AAAAMMJJHHMMSS (heure fiscale) → JJ/MM/AAAA. */
const dateFiscale = (d: string | null): string => (d && d.length >= 8 ? `${d.slice(6, 8)}/${d.slice(4, 6)}/${d.slice(0, 4)}` : '—');
const dateEpoch = (ms: number | null): string => (ms ? new Date(ms).toLocaleDateString('fr-FR') : '—');
/**
 * AAAAMM → « août 25 » (l'axe du graphe des 12 mois).
 * 🔴 LE NOM DU MOIS VIENT D’`Intl`, JAMAIS D’UNE TABLE ÉCRITE À LA MAIN. La table qui vivait ici
 *    était en français pur : la migration en a fait passer TROIS entrées sur douze par `t()` —
 *    celles que le détecteur reconnaissait comme des mots — et laissé les neuf autres en dur.
 *    Traduire un calendrier à la main, c'est la même faute que d'écrire un seuil de pluriel chez
 *    soi : la plateforme sait déjà le faire, pour toutes les langues, et sans qu'on l'entretienne.
 * ⚠️ LU AU RENDU (`currentLang()`), jamais capturé dans une constante de module.
 */
const moisCourt = (aaaamm: string): string => {
  const mois = Number(aaaamm.slice(4, 6));
  if (!Number.isFinite(mois) || mois < 1 || mois > 12) return `? ${aaaamm.slice(2, 4)}`;
  const nom = new Intl.DateTimeFormat(currentLang(), { month: 'short' }).format(new Date(Date.UTC(2000, mois - 1, 1)));
  return `${nom} ${aaaamm.slice(2, 4)}`;
};

/**
 * Saisie TOLÉRANTE de la naissance → ISO, DÉLÉGUÉE au cœur (`parseBirthdate`, §28.1). Le tri-état est
 * celui de l'écran : `null` = champ vide ; `undefined` = ILLISIBLE (l'appelant refuse — jamais un silence).
 */
export function parseNaissance(saisie: string): string | null | undefined {
  const trimmed = saisie.trim();
  if (!trimmed) return null;
  // ⚠️ L'ISO N'EST PLUS RETOURNÉ AVANT L'APPEL : le cœur l'accepte désormais lui-même. Ce
  //    contournement était le seul indice que la caisse, elle, perdait ses dates.
  return parseBirthdate(trimmed) ?? undefined;
}

/**
 * Les onglets — MÊMES ids, MÊME ordre et MÊMES ICÔNES que la caisse (`client-card.tsx`), + les deux
 * qui lui sont propres (Qualifications, RGPD).
 *
 * ⚠️ ILS DÉSIGNENT L'ICÔNE PAR SON NOM, PLUS PAR SON TRACÉ (2026-09-06). Cette table portait des
 * chemins SVG écrits à la main, et son commentaire promettait déjà « les mêmes dessins que la
 * caisse » — c'était faux DEUX FOIS : la caisse passe par la charte (`<Icon name="user">`), et elle
 * n'avait pas choisi les mêmes icônes (l'acompte y est un portefeuille, pas un signe dollar ; le
 * coupon un pourcentage, pas un ticket). Un tracé recopié ne suit pas la charte quand elle change :
 * l'application aurait fini par afficher deux versions de la même icône, sans qu'on sache où sont
 * les copies. Mesuré par `scripts/audit-icones-code.mjs`.
 */
const ONGLETS: readonly { id: string; l: Translatable; icon: IconName }[] = [
  { id: 'gen', l: key('Général'), icon: 'user' },
  { id: 'qualif', l: key('Qualifications'), icon: 'tag' },
  { id: 'wallet', l: key('Wallet'), icon: 'wallet' },
  { id: 'hist', l: key('Historique'), icon: 'history' },
  { id: 'av', l: key('Avoirs'), icon: 'gift' },
  { id: 'stats', l: key('Statistiques'), icon: 'chart' },
  // `shield` et non `lock` : le verrou dit « on ne peut pas y toucher », le bouclier « c'est protégé ».
  { id: 'rgpd', l: 'RGPD', icon: 'shield' },
] as const;
type OngletId = (typeof ONGLETS)[number]['id'];

/** Le brouillon d'édition — les champs FERMÉS de l'onglet Général (naissance encore en texte libre). */
interface Brouillon {
  title: string; lastName: string; firstName: string; email: string; mobilePhone: string; landlinePhone: string;
  birthdate: string; address1: string; postalCode: string; city: string; permanentDiscount: string;
  isWeb: boolean; isPro: boolean; companyLegalName: string; siret: string; vatNumber: string; naf: string;
  optInEmail: boolean; optInSms: boolean; optInWallet: boolean; qualifications: string[];
}
const BROUILLON_VIDE: Brouillon = {
  title: 'M.', lastName: '', firstName: '', email: '', mobilePhone: '', landlinePhone: '',
  birthdate: '', address1: '', postalCode: '', city: '', permanentDiscount: '',
  isWeb: false, isPro: false, companyLegalName: '', siret: '', vatNumber: '', naf: '',
  // ⚠️ E-MAIL ET SMS ACTIFS SUR UNE FICHE NEUVE (décision utilisateur 2026-08-30, alignée ici) : le
  //    vendeur décoche si le client refuse. ⚠️ LE WALLET, LUI, RESTE À FAUX : il ne s'active pas d'un
  //    trait de plume — c'est la CRÉATION DU PASS qui vaut consentement, et elle se fait à part.
  optInEmail: true, optInSms: true, optInWallet: false, qualifications: [],
};

const sectionHeading = { fontFamily: MONO, fontSize: '10.5px', letterSpacing: '.09em', textTransform: 'uppercase' as const, color: 'var(--ink-500)', fontWeight: 600, marginBottom: 9 };
const etiquette = { display: 'block', fontSize: '11px', fontWeight: 600, color: 'var(--ink-500)', marginBottom: 5 };
const champ = { width: '100%', height: 38, padding: '0 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', fontSize: '13px', color: 'var(--ink-900)', boxSizing: 'border-box' as const };
/**
 * En-tête TRIABLE d'une colonne de l'historique — le geste des autres tableaux du back-office, posé
 * sur le tableau de la caisse.
 */
function ThHist({ label, k, align, tri, onTri }: {
  label: string;
  k: 'at' | 'label' | 'size' | 'qty' | 'total';
  align?: 'center' | 'right';
  tri: { key: string; dir: 'asc' | 'desc' };
  onTri: (t: { key: 'at' | 'label' | 'size' | 'qty' | 'total'; dir: 'asc' | 'desc' }) => void;
}) {
  const actif = tri.key === k;
  return (
    <button type="button"
      onClick={() => onTri({ key: k, dir: actif && tri.dir === 'desc' ? 'asc' : 'desc' })}
      style={{
        border: 'none', background: 'none', padding: 0, cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 4,
        justifyContent: align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start',
        font: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit',
        color: actif ? 'var(--teal-700)' : 'inherit',
      }}>
      {label}
      <span aria-hidden style={{ fontSize: '8px', opacity: actif ? 1 : 0.35 }}>{actif ? (tri.dir === 'asc' ? '▲' : '▼') : '▲▼'}</span>
    </button>
  );
}

/** Le CADRE d'un graphe de statistiques — celui de la caisse : carte blanche, titre, sous-titre. */
const carteStat = { padding: 20, border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', background: 'var(--paper)' };
const titreStat = { fontSize: '15px', fontWeight: 600, color: 'var(--ink-900)', marginBottom: 4 };
const sousTitreStat = { fontSize: '12px', color: 'var(--ink-500)', marginBottom: 18 };
const noteGrise = { display: 'flex', gap: 8, alignItems: 'flex-start', padding: '10px 12px', borderRadius: 'var(--r-ctrl)', background: 'var(--ink-50)', fontSize: '11.5px', color: 'var(--ink-500)', lineHeight: 1.55 };

/** L'info d'une note grise — l'icône de la charte, alignée sur la première ligne de texte. */
function IconInfo() {
  return <Icon name="info" size={13} stroke={2} style={{ flexShrink: 0, marginTop: 1 }} />;
}

/**
 * LE DÉTAIL DE LA CAGNOTTE — le grand livre, en modale, EXACTEMENT comme en caisse
 * (`LoyaltyLedgerModal`) : on l'ouvre depuis le montant des gains, pas depuis un onglet. Exporté pour être
 * RENDU par son banc : le total et ses deux avertissements sont des choix d'affichage.
 */
export function DetailCagnotte({ detail, onClose }: { detail: ClientDetail; onClose: () => void }) {
  useEffect(() => {
    // ⚠️ CAPTURE (`true`) : sans elle, Échap fermerait AUSSI la fiche derrière — deux modales,
    //    un seul geste, l'utilisateur perdrait sa saisie en refermant le grand livre.
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 950, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} role="dialog" aria-modal="true" aria-label={t('Détail de la cagnotte')}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(12,14,16,.42)' }} aria-hidden />
      <div style={{ position: 'relative', width: 'min(94vw, 560px)', maxHeight: '80vh', display: 'flex', flexDirection: 'column', background: 'var(--paper)', borderRadius: 'var(--r-lg)', boxShadow: '0 28px 70px -10px rgba(0,0,0,.45)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderBottom: '1px solid var(--ink-200)' }}>
          <span style={{ width: 44, height: 44, borderRadius: 'var(--r-ctrl)', background: 'var(--teal-100)', color: 'var(--teal-700)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon name="star" size={20} stroke={2} />
          </span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: '15px', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Détail de la cagnotte')}</span>
            <span style={{ display: 'block', fontSize: '12px', color: 'var(--ink-500)' }}>{t('Grand livre des gains — solde actuel {v}', { v: euros(detail.record.loyaltyCents) })}</span>
            {/* ⚠️ LE MOTIF EST ÉCRIT EN CLAIR ICI, pas seulement au survol (audit F22) : c'est l'écran où le
                commerçant vient CHERCHER l'explication, et le grand livre juste en dessous la démontre. */}
            {merchantBalanceView(detail.record.loyaltyCents, detail.record.negativeCause).reason && (
              <span style={{ display: 'block', marginTop: 3, fontSize: '11.5px', color: 'var(--danger-ink)' }}>
                {merchantBalanceView(detail.record.loyaltyCents, detail.record.negativeCause).reason}
              </span>
            )}
          </span>
          <button type="button" onClick={onClose} aria-label={t('Fermer')}
            style={{ width: 32, height: 32, border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--ink-100)', color: 'var(--ink-600)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <Icon name="x" size={14} stroke={2.2} />
          </button>
        </div>
        <div style={{ overflowY: 'auto', padding: '6px 0' }}>
          {detail.ledger.length === 0 && (
            <div style={{ padding: '28px 18px', textAlign: 'center', fontSize: '13px', color: 'var(--ink-500)' }}>
              
              {t('Aucun mouvement de cagnotte pour l’instant.')}
            </div>
          )}
          {detail.ledger.map((l, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 18px', borderBottom: i < detail.ledger.length - 1 ? '1px solid var(--ink-100)' : 'none' }}>
              <span style={{ width: 110, fontSize: '12px', color: 'var(--ink-500)', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{dateEpoch(l.at)}</span>
              <span style={{ flex: 1, fontSize: '13px', fontWeight: 500, color: 'var(--ink-800)' }}>{l.label}</span>
              <span style={{ fontSize: '14px', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: l.deltaCents >= 0 ? 'var(--teal-700)' : 'var(--rose-fg)' }}>
                {l.deltaCents >= 0 ? '+' : '−'}{euros(Math.abs(l.deltaCents))}
              </span>
            </div>
          ))}
          {/* ⚠️ LE TOTAL DE TOUT LE GRAND LIVRE, calculé par le serveur et confronté au solde (D12) : une liste
              tronquée se DIT, un écart aussi — et le total, lui, n'est jamais tronqué. */}
          {detail.ledger.length > 0 && (() => {
            const { totalCents, gapCents } = ledgerTotalView(detail.ledgerTotalCents, detail.record.loyaltyCents);
            return (
              <div data-testid="grand-livre-total" style={{ padding: '10px 18px', borderTop: '2px solid var(--ink-200)', display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ flex: 1, fontSize: '13px', fontWeight: 700, color: 'var(--ink-700)' }}>
                    {t('Total des mouvements')}
                  </span>
                  <span style={{ fontSize: '14px', fontWeight: 800, color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>{euros(totalCents)}</span>
                </div>
                {detail.ledgerTruncated && (
                  <span style={{ fontSize: '11.5px', color: 'var(--ink-500)' }}>{t('Seuls les mouvements les plus récents sont affichés ; le total porte sur tout l’historique.')}</span>
                )}
                {gapCents !== 0 && (
                  <span style={{ fontSize: '11.5px', color: 'var(--danger-ink)' }}>{t('Écart de {v} entre le total des mouvements et le solde enregistré.', { v: euros(Math.abs(gapCents)) })}</span>
                )}
              </div>
            );
          })()}
        </div>
        <div style={{ ...noteGrise, borderTop: '1px solid var(--ink-100)', borderRadius: 0 }}>
          <IconInfo />
          <span><Trans k='Grand livre <1>append-only</1>, réconcilié entre les caisses — le solde en est la somme. Les règles du programme (mode, taux, paliers) se configurent dans <2>Réglages → Fidélité</2> : elles valent pour le magasin, pas pour ce client.' parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} /></span>
        </div>
      </div>
    </div>
  );
}

/**
 * La modale. `id === null` = CRÉATION (rail réduit à Général, bouton « Créer le client ») ;
 * sinon la fiche se charge puis s'édite. `onDone(reload)` ferme — reload=true si la liste doit relire.
 */
export function FicheClient({ id, onDone }: { id: string | null; onDone: (reload: boolean) => void }) {
  /** Le préfixe des identifiants qui relient chaque champ au texte qui le présente. */
  const fieldId = useId();
  const creation = id === null;
  const navigate = useNavigate();
  const [detail, setDetail] = useState<ClientDetail | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [onglet, setOnglet] = useState<OngletId>('gen');
  // ⚠️ FILTRE DE PÉRIODE, comme en caisse (demande utilisateur 2026-09-06) : il manquait, et sans lui
  //    une fiche de dix ans s'ouvre sur dix ans. La règle est celle de la caisse — la clé AAAAMM de la
  //    pièce, jamais une date recalculée ici.
  const [periode, setPeriode] = useState<'all' | 'month' | 'year'>('all');
  // ⚠️ RECHERCHE, FILTRE ET TRI DES BONS (demande utilisateur 2026-09-06) : la caisse les avait, le
  //    back-office non — sur une fiche ancienne, retrouver un n° ou le bon qui expire le premier se
  //    faisait à l'œil. Mêmes gestes des deux côtés.
  // ⚠️ TRI PAR LES EN-TÊTES (demande utilisateur 2026-09-06), comme partout ailleurs au back-office.
  //    Le tableau reprend la forme de la caisse ; c'est le GESTE qui vient du back-office.
  const [triHist, setTriHist] = useState<{ key: 'at' | 'label' | 'size' | 'qty' | 'total'; dir: 'asc' | 'desc' }>({ key: 'at', dir: 'desc' });
  const [rechBon, setRechBon] = useState('');
  const [filtreBon, setFiltreBon] = useState('all');
  const [triBon, setTriBon] = useState<{ key: 'code' | 'issued' | 'expires' | 'state' | 'amount'; dir: 'asc' | 'desc' }>({ key: 'issued', dir: 'desc' });
  /** Les lignes VISIBLES — le filtre porte sur la clé AAAAMM de la pièce, la règle de la caisse. */
  const moisCourant = ((): string => { const d = new Date(); return String(d.getFullYear()) + String(d.getMonth() + 1).padStart(2, '0'); })();
  const anneeCourante = String(new Date().getFullYear());
  /** ⚠️ L'ORDRE DES ÉTATS EST CELUI DE L'USAGE, pas l'alphabet : ce qu'on peut prendre d'abord. */
  const RANG_ETAT: Record<string, number> = { issued: 0, partial: 1, redeemed: 2, expired: 3 };
  const typesBonsPresents = [...new Set((detail?.vouchers ?? []).map((v) => v.type))].sort();
  const bonsVisibles = (detail?.vouchers ?? [])
    .filter((v) => (filtreBon === 'all' || v.type === filtreBon)
      && (!rechBon || v.code.toLowerCase().includes(rechBon.toLowerCase())))
    .slice()
    .sort((a, b) => {
      const sens = triBon.dir === 'asc' ? 1 : -1;
      if (triBon.key === 'code') return sens * a.code.localeCompare(b.code);
      // ⚠️ « Sans échéance » va TOUJOURS au bout : un bon sans date de fin n'est ni le plus proche ni
      //    le plus lointain — il est hors de la question posée.
      if (triBon.key === 'expires') {
        if (a.expiresAt == null || b.expiresAt == null) return (a.expiresAt == null ? 1 : 0) - (b.expiresAt == null ? 1 : 0);
        return sens * (a.expiresAt - b.expiresAt);
      }
      if (triBon.key === 'state') return sens * ((RANG_ETAT[a.state] ?? 9) - (RANG_ETAT[b.state] ?? 9));
      if (triBon.key === 'amount') return sens * (a.amountCents - b.amountCents);
      return sens * (a.issuedAt - b.issuedAt);
    });
  const histVisible = (detail?.history ?? [])
    .filter((l) => (periode === 'month' ? l.at.startsWith(moisCourant) : periode === 'year' ? l.at.startsWith(anneeCourante) : true))
    .slice()
    .sort((a, b) => {
      const sens = triHist.dir === 'asc' ? 1 : -1;
      switch (triHist.key) {
        case 'label': return sens * a.label.localeCompare(b.label);
        // ⚠️ Une taille est un LIBELLÉ (« 38 », « M », « U ») : on la compare en respectant les
        //    chiffres, sinon « 10 » se range avant « 8 ». Les tailles absentes vont au bout.
        case 'size': {
          if (!a.size || !b.size) return (a.size ? 0 : 1) - (b.size ? 0 : 1);
          return sens * a.size.localeCompare(b.size, 'fr', { numeric: true });
        }
        case 'qty': return sens * (a.qty - b.qty);
        case 'total': return sens * (a.totalCents - b.totalCents);
        default: return sens * a.at.localeCompare(b.at);
      }
    });
  const [passEnCours, setPassEnCours] = useState(false);
  /** État du connecteur wallet du magasin — `null` tant qu'on ne l'a pas lu (on n'affirme rien). */
  const [walletPret, setWalletPret] = useState<boolean | null>(null);
  /** Le programme est-il ACTIVÉ pour ce magasin ? Distinct de la clé — le refus ne se dit pas pareil. */
  const [walletActif, setWalletActif] = useState(true);

  /**
   * ÉMET LA CARTE du client chez Eldowallet, avec la clé API du magasin.
   *
   * ⚠️ CE QUI REVIENT DÉCIDE DU MESSAGE, jamais l'inverse : le serveur vérifie d'abord la fiche
   * (e-mail, numéro de carte, consentement) puis appelle le fournisseur. Un refus porte le motif
   * RÉEL — celui qui dit quel champ remplir, ou ce que le portefeuille a répondu.
   *
   * ⚠️ ON RECHARGE LA FICHE APRÈS : l'identité rendue par Eldowallet vient d'être retenue côté
   * serveur, et c'est elle qui rendra possibles les gains, les coupons et les notifications.
   * Afficher « émis » sans relire laisserait l'écran affirmer un état qu'il n'a pas constaté.
   */
  /**
   * L'INTERRUPTEUR FAIT TOUT — activer, c'est consentir ET créer la carte (un seul geste).
   *
   * ⚠️ L'ÉTAT AFFICHÉ SUIT LE SERVEUR, jamais l'intention. On bascule l'affichage seulement APRÈS la
   * réponse : un interrupteur qui passe au vert avant de savoir affirmerait une carte qui n'existe
   * peut-être pas (§23.0). En cas de refus, il revient exactement où il était, avec le motif réel.
   * ⚠️ Il reste VERROUILLÉ pendant l'appel — c'est ce qui montre que le travail est en cours.
   */
  const basculerWallet = async (actif: boolean) => {
    if (!id || passEnCours) return;
    // ⚠️ DÉSACTIVER SE CONFIRME (demande utilisateur 2026-08-31). Ce n'est pas un réglage qu'on
    //    rebascule sans conséquence : la carte du client cesse d'être alimentée, et la remettre en
    //    service demandera un nouvel appel au fournisseur. La confirmation dit AUSSI ce qui ne se
    //    passe pas — le pass n'est pas supprimé du téléphone — pour qu'on décide en connaissance.
    if (!actif && !(await confirm({
      title: t('Désactiver la carte dématérialisée ?'),
      message: t('Elle ne recevra plus les gains, coupons ni notifications. Le pass déjà installé sur le téléphone du client n’est pas supprimé.'),
      confirmLabel: t('Désactiver'),
    }))) return;
    setPassEnCours(true);
    try {
      const r = await api.setClientWallet(id, actif);
      setD((x) => ({ ...x, optInWallet: r.active }));
      showToast(
        !actif
          ? t('Carte désactivée : elle ne sera plus alimentée. Le pass du client n’est pas supprimé.')
          : r.alreadyExists
            ? t('Cette carte existait déjà chez Eldowallet — la fiche y est désormais rattachée.')
            : t('Carte créée. Eldowallet envoie lui-même le lien au client.'),
        'ok', 7000,
      );
      load();
    } catch (e) {
      // L'interrupteur NE bouge pas : le serveur n'a rien écrit, l'écran ne doit rien affirmer.
      showToast(t('Carte non créée : {v}', { v: e instanceof Error ? e.message : String(e) }), 'err', 9000);
    } finally { setPassEnCours(false); }
  };
  const [d, setD] = useState<Brouillon>(BROUILLON_VIDE);
  const [enregistrement, setEnregistrement] = useState(false);
  const [cagnotte, setCagnotte] = useState(false);
  /** Le référentiel des qualifications — lu UNE fois à l'ouverture (le champ en a besoin). */
  const [qualifs, setQualifs] = useState<ClientQualification[]>([]);

  // ⚠️ Best-effort assumé : un référentiel illisible ne doit pas empêcher d'ouvrir une fiche. Le champ
  //    se replie alors sur la seule valeur portée par la fiche — jamais une liste vide silencieuse.
  useEffect(() => { api.qualifications().then((r) => setQualifs(r.rows)).catch(() => undefined); }, []);

  // ⚠️ SANS CLÉ API, L'INTERRUPTEUR WALLET EST INERTE (demande utilisateur 2026-08-31) : proposer
  //    d'activer une carte que le magasin ne peut pas créer serait offrir une option qui n'existe pas
  //    (§23.0). L'état est LU, jamais supposé — et tant qu'on ne l'a pas, on n'affirme ni l'un ni l'autre.
  useEffect(() => {
    // ⚠️ DEUX CONDITIONS, PAS UNE (2026-09-06) : une clé API POSÉE ne veut pas dire un programme
    //    ACTIF. Un magasin peut suspendre son wallet sans démonter son intégration — la caisse a ce
    //    réglage depuis toujours, et le siège l'ignorait. Brancher n'est pas activer.
    void Promise.all([
      api.integrationWallet().then((e) => e.token === 'ok').catch(() => false),
      api.storeSettings()
        .then((r) => {
          const v = r.settings.find((x) => x.name === 'wallet.enabled')?.value;
          // ⚠️ LA LECTURE DE LA CAISSE, prise au contrat : absent = activé (ne rien avoir décidé n'est pas
          //    avoir dit non), posé = « 1 » seul vaut activé — la lecture tolérante déclarait actif ce que la
          //    caisse lit éteint.
          return switchOn('wallet.enabled', v ?? null);
        })
        .catch(() => true),
    ]).then(([cle, actif]) => { setWalletPret(cle && actif); setWalletActif(actif); });
  }, []);
  /** Saisie de l'ajout au référentiel depuis l'onglet Qualifications. */
  const [nouvelleQualif, setNouvelleQualif] = useState('');

  /**
   * Ce que l'onglet PROPOSE : les qualifications actives, PLUS celles que la fiche porte déjà.
   * ⚠️ Une valeur désactivée — ou disparue du référentiel — reste offerte SI la fiche la porte :
   *    sinon l'enregistrement la perdrait en silence, et le magasin ne saurait même pas laquelle.
   */
  const optionsQualif = useMemo(() => {
    const actives = qualifs.filter((x) => x.isActive || d.qualifications.includes(x.code))
      .map((x) => ({ code: x.code, label: x.isActive ? x.label : t('{label} (désactivée)', { label: x.label }), color: x.color }));
    const orphelines = d.qualifications
      .filter((c) => !qualifs.some((x) => x.code === c))
      .map((c) => ({ code: c, label: t('{c} (hors référentiel)', { c }), color: null as string | null }));
    return [...actives, ...orphelines];
  }, [qualifs, d.qualifications]);

  /** Ce que le client PORTE (zone du haut) et ce que le magasin PROPOSE encore (zone du bas). */
  const posees = useMemo(
    () => d.qualifications.map((c) => optionsQualif.find((o) => o.code === c) ?? { code: c, label: c, color: null }),
    [d.qualifications, optionsQualif],
  );
  const aProposer = useMemo(
    () => optionsQualif.filter((o) => !d.qualifications.includes(o.code)),
    [optionsQualif, d.qualifications],
  );

  /** Créer une qualification au référentiel ET la poser sur la fiche — l'échec se dit. */
  const creerQualif = async () => {
    const code = nouvelleQualif.trim();
    if (!code) return;
    try {
      await api.saveQualification({ code, label: code, sortOrder: qualifs.length });
      const r = await api.qualifications();
      setQualifs(r.rows);
      setD((x) => (x.qualifications.includes(code) ? x : { ...x, qualifications: [...x.qualifications, code] }));
      setNouvelleQualif('');
      showToast(t('Qualification « {code} » ajoutée au référentiel et posée sur la fiche.', { code }));
    } catch (e) {
      showToast(t('La qualification n’a pas pu être créée : {v}', { v: e instanceof Error ? e.message : String(e) }), 'err');
    }
  };

  // ⚠️ SEULE LA DERNIÈRE LECTURE REMPLIT LA FICHE : celle du client précédent, revenue en dernier, remplirait le
  //    formulaire de ses données — et « Enregistrer » les écrirait sur le client affiché.
  const latest = useLatest();
  const load = useCallback(async () => {
    if (id === null) return;
    const isLatest = latest();
    try {
      const det = await api.client(id);
      if (!isLatest()) return;
      setDetail(det); setFailure(null);
      const f = det.record;
      setD({
        // ⚠️ L'écran affiche CE QUE LA BASE TIENT (Forme I) : la normalisation des civilités vit aux
        //    PORTES d'écriture (reprise + serveur) — un résidu long d'avant-rejeu rend en 3ᵉ segment.
        title: f.title ?? '', lastName: f.lastName, firstName: f.firstName ?? '', email: f.email ?? '',
        mobilePhone: f.mobilePhone ?? '', landlinePhone: f.landlinePhone ?? '',
        birthdate: f.birthdate ? bdL(f.birthdate) : '', address1: f.address1 ?? '',
        postalCode: f.postalCode ?? '', city: f.city ?? '', permanentDiscount: f.permanentDiscountBp ? formatRateBp(f.permanentDiscountBp, false) : '',
        isWeb: f.isWeb, isPro: f.isPro, companyLegalName: f.companyLegalName ?? '',
        siret: f.siret ?? '', vatNumber: f.vatNumber ?? '', naf: f.naf ?? '',
        optInEmail: f.optInEmail, optInSms: f.optInSms, optInWallet: f.optInWallet,
        qualifications: (f.qualifications ?? []).map((q) => q.code),
      });
    } catch (e) {
      if (isLatest()) setFailure(e instanceof Error ? e.message : String(e));
    }
  }, [id, latest]);
  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !cagnotte) onDone(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onDone, cagnotte]);

  const f = detail?.record ?? null;
  const anonyme = f?.erased ?? false;
  const nom = creation ? t('Nouveau client') : anonyme ? t('Client anonymisé') : `${f?.lastName ?? ''} ${f?.firstName ?? ''}`.trim();
  const initiales = creation ? '+' : anonyme ? '×' : `${f?.lastName?.[0] ?? ''}${f?.firstName?.[0] ?? ''}`.toUpperCase();
  const palier = f?.tierDisplay ? palierPastille(f.tierDisplay) : null;
  // ⚠️ CE QUE LE COMMERÇANT VOIT quand la cagnotte est négative (audit F22) : le vrai montant, et le
  //    motif au survol. Le CLIENT, lui, ne verra jamais ce signe — le wallet et les canaux marketing
  //    passent par `customerFacingBalance`, qui plafonne à 0.
  const balanceReason = f ? merchantBalanceView(f.loyaltyCents, f.negativeCause).reason : null;
  const isNegative = balanceReason !== null;
  // ⚠️ L'ANCIENNETÉ EST LA RÈGLE DU CŒUR (`clientSinceYear`), la même qu'en caisse : le premier achat, sinon la fiche.
  const sinceYear = detail && f ? clientSinceYear(detail.historyStats.firstAt, f.createdAt) : null;
  const subheading = creation
    ? t('Carte générée à l’enregistrement — dédup automatique sur mobile et email.')
    // ⚠️ SANS LA CARTE : elle EST le n° client, l'afficher à côté répétait le même numéro.
    : f ? t('{v} · client depuis {v2} · dernière visite {v3}', { v: f.clientNumber ?? '—', v2: sinceYear ?? '—', v3: dateFiscale(detail!.historyStats.lastAt) }) : '';
  const onglets = creation ? ONGLETS.slice(0, 1) : ONGLETS;

  /** La saisie du brouillon → le corps de requête (naissance tolérante VALIDÉE — jamais avalée). */
  const corps = (): ClientInput | null => {
    const naissance = parseNaissance(d.birthdate);
    if (naissance === undefined) {
      showToast(t('Date de naissance illisible : « {v} » — formats acceptés 01011990, 01/01/1990…', { v: d.birthdate.trim() }), 'err');
      return null;
    }
    // ⚠️ UN TAUX ILLISIBLE EST REFUSÉ, JAMAIS CORRIGÉ : « 12,555 » ou « 101 » ne deviennent pas un autre
    //    taux en silence (`parseRateBp`, le même que la caisse).
    const remise = parseRateBp(d.permanentDiscount);
    if (remise === null) {
      showToast(t('Remise permanente illisible : « {v} » — un taux de 0 à 100 %, deux décimales au plus.', { v: d.permanentDiscount.trim() }), 'err');
      return null;
    }
    return {
      title: d.title || null, lastName: d.lastName, firstName: d.firstName || null,
      email: d.email || null, mobilePhone: d.mobilePhone || null, landlinePhone: d.landlinePhone || null,
      birthdate: naissance, address1: d.address1 || null, postalCode: d.postalCode || null, city: d.city || null,
      permanentDiscountBp: remise, isWeb: d.isWeb, isPro: d.isPro,
      companyLegalName: d.companyLegalName || null, siret: d.siret || null, vatNumber: d.vatNumber || null, naf: d.naf || null,
      optInEmail: d.optInEmail, optInSms: d.optInSms, optInWallet: d.optInWallet,
      qualifications: d.qualifications,
    };
  };

  const save = async () => {
    if (enregistrement) return;
    if (!d.lastName.trim()) { showToast(t('Nom requis — la fiche n’a pas été enregistrée.'), 'err'); return; }
    const input = corps();
    if (!input) return;
    setEnregistrement(true);
    try {
      if (creation) {
        try {
          await api.createClient(input);
        } catch (e) {
          // 409 = doublon PROBABLE (catalogue du magasin) : le serveur NOMME la fiche, l'humain tranche.
          if (e instanceof ApiError && e.status === 409) {
            const quandMeme = await confirm({
              title: t('Ce client existe peut-être déjà'), message: e.message,
              confirmLabel: t('Créer quand même'), tone: 'info',
            });
            if (!quandMeme) return;
            await api.createClient(input, true);
          } else { throw e; }
        }
        showToast(t('Client créé au fichier — {v} {v2}', { v: input.lastName.toUpperCase(), v2: input.firstName ?? '' }).trim());
      } else {
        await api.saveClient(id!, input);
        showToast(t('Fiche client enregistrée — {v} {v2}', { v: input.lastName.toUpperCase(), v2: input.firstName ?? '' }).trim());
      }
      onDone(true);
    } catch (e) {
      showToast(e instanceof Error ? e.message : String(e), 'err');
    } finally {
      setEnregistrement(false);
    }
  };

  const anonymiser = async () => {
    if (!f || anonyme) return;
    const ok = await confirm({
      title: t('Anonymiser la fiche de {nom} ?', { nom }),
      message: t('Identité, contacts et consentements seront effacés, la cagnotte est soldée. Les pièces fiscales et les mouvements restent scellés — l’historique devient orphelin. Irréversible.'),
      confirmLabel: t('Anonymiser'), tone: 'danger', requireText: f.lastName,
    });
    if (!ok) return;
    try {
      await api.anonymizeClient(f.id);
      showToast(t('Fiche anonymisée — identité détachée, pièces conservées ({v})', { v: f.clientNumber ?? f.id }));
      onDone(true);
    } catch (e) {
      showToast(e instanceof Error ? e.message : String(e), 'err');
    }
  };

  /** Export RGPD RÉEL : tout ce que la fiche détient, en un CSV long format (rubrique / champ / valeur). */
  const exporterDonnees = () => {
    if (!detail || !f) return;
    const lines: (readonly [string, string, string])[] = [
      [t('Identité'), t('Nom'), f.lastName], [t('Identité'), t('Prénom'), f.firstName ?? ''], [t('Identité'), t('Civilité'), f.title ?? ''],
      [t('Identité'), t('N° client'), f.clientNumber ?? ''], [t('Identité'), t('Carte de fidélité'), f.loyaltyNumber ?? ''],
      [t('Identité'), t('Naissance'), f.birthdate ?? ''],
      [t('Identité'), t('Qualifications'), (f.qualifications ?? []).map((q) => q.label ?? q.code).join(' · ')],
      [t('Contact'), t('Email'), f.email ?? ''], [t('Contact'), t('Mobile'), f.mobilePhone ?? ''], [t('Contact'), t('Fixe'), f.landlinePhone ?? ''],
      [t('Contact'), t('Adresse'), [f.address1, f.postalCode, f.city].filter(Boolean).join(', ')],
      [t('Consentements'), t('Email'), f.optInEmail ? 'opt-in' : 'opt-out'],
      [t('Consentements'), 'SMS', f.optInSms ? 'opt-in' : 'opt-out'],
      [t('Consentements'), t('Wallet'), f.optInWallet ? 'opt-in' : 'opt-out'],
      [t('Consentements'), t('Dernier enregistrement'), f.consentAt ? `${dateEpoch(f.consentAt)} · ${f.consentChannel ?? ''} · ${f.consentBy ?? ''}` : 'aucun horodatage'],
      // ⚠️ ICI, LE VRAI MONTANT — SIGNE COMPRIS, et c'est la SEULE surface tournée vers la personne qui
      //    échappe au plafonnement à 0 de `customerFacingBalance` (audit F22). Une demande d'accès RGPD
      //    (art. 15) porte sur les données RÉELLEMENT traitées : y écrire 0 quand la base tient −4,20 €
      //    serait une déclaration fausse dans un document légal. Le grand livre suit juste en dessous et
      //    démontre le chiffre ligne par ligne — masquer le total le contredirait de toute façon.
      [t('Fidélité'), t('Cagnotte ({code})', { code: currentCurrency().code }), toInputValue(f.loyaltyCents)],
      [t('Fidélité'), t('Palier'), palier?.l ?? ''],
      ...detail.ledger.map((l) => [t('Fidélité — grand livre'), dateEpoch(l.at), `${l.label} : ${formatMoney(l.deltaCents)}`] as const),
      ...detail.history.map((h) => [t('Achats'), `${h.pieceNumber} · ${dateFiscale(h.at)}`, `${h.label} ×${h.qty} — ${formatMoney(h.totalCents)}`] as const),
      ...detail.vouchers.map((t2) => [t('Titres'), t2.code, `${typeUi(t2.type).l} · ${formatMoney(t2.amountCents)} · ${ETAT_TITRE[t2.state].l.toLowerCase()}`] as const),
    ];
    exporterCsv('client-rgpd', [t('Rubrique'), t('Champ'), t('Valeur')], lines.map((l) => [...l]));
    showToast(t('Export des données du client généré (.csv) — {nom}', { nom }));
  };

  const bindField = (k: keyof Brouillon) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setD((x) => ({ ...x, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  // Les CODES de `core-crm`, les mêmes qu'en caisse — une valeur existante hors liste (« Mademoiselle »,
  // « Dr », une reprise non normalisée) reste choisissable et s'affiche telle quelle : la perdre
  // effacerait ce qui a été déclaré.
  const civilites: string[] = [...CIVILITY_CODES, ...(d.title && !isCivilityCode(d.title) ? [d.title] : [])];
  const stats = detail?.historyStats;
  // ⚠️ `divRoundHalfUp` : un chiffre d'affaires NET peut être négatif (plus de retours que de
  //    ventes sur la période), et `Math.round` arrondit vers +∞ sur les négatifs.
  const panierMoyen = stats && stats.tickets > 0 ? divRoundHalfUp(stats.caCents, stats.tickets) : 0;
  const maxMois = Math.max(1, ...(detail?.monthlySpend ?? []).map((m) => m.totalCents));
  const totalFamilles = (detail?.familyBreakdown ?? []).reduce((somme, x) => somme + x.totalCents, 0);
  const COULEURS_FAMILLE = ['var(--teal-500)', 'oklch(0.62 0.13 240)', 'oklch(0.72 0.13 60)', 'oklch(0.65 0.14 290)', 'var(--ink-400)'];

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} role="dialog" aria-modal="true" aria-label={nom || t('Fiche client')}>
      <div onClick={() => onDone(false)} style={{ position: 'absolute', inset: 0, background: 'rgba(12,14,16,.42)' }} aria-hidden />
      <div style={{ position: 'relative', width: 'min(96vw, 930px)', height: 'min(88vh, 660px)', background: 'var(--paper)', borderRadius: 'var(--r-lg)', boxShadow: '0 28px 70px -10px rgba(0,0,0,.45)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* ── L'en-tête ──────────────────────────────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 20px', borderBottom: '1px solid var(--ink-200)' }}>
          <span style={{ width: 50, height: 50, borderRadius: 'var(--r-pill)', background: anonyme ? 'var(--ink-100)' : 'var(--teal-100)', color: anonyme ? 'var(--ink-400)' : 'var(--teal-700)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', fontWeight: 700, flexShrink: 0 }}>{initiales}</span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: '18px', fontWeight: 700, letterSpacing: '-0.015em', color: 'var(--ink-900)' }}>{nom}</span>
              {palier && !anonyme && <span style={{ height: 20, padding: '0 9px', borderRadius: 'var(--r-pill)', background: palier.bg, color: palier.fg, fontSize: '10.5px', fontWeight: 700, display: 'inline-flex', alignItems: 'center' }}>{palier.l}</span>}
              {/* ⚠️ LE BADGE DIT LE PASS, PAS LE CONSENTEMENT, comme en caisse : consentir n'émet rien. */}
              {detail && walletBadgeOn(detail.wallet) && !anonyme && <span data-testid="client-wallet-badge" style={{ height: 20, padding: '0 9px', borderRadius: 'var(--r-pill)', background: 'var(--amber-bg)', color: 'var(--amber-fg)', fontSize: '10.5px', fontWeight: 700, display: 'inline-flex', alignItems: 'center' }}>{t('Wallet actif')}</span>}
              {anonyme && <span style={{ height: 20, padding: '0 9px', borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', color: 'var(--ink-500)', fontSize: '10.5px', fontWeight: 700, display: 'inline-flex', alignItems: 'center' }}>{t('Anonymisé')}</span>}
            </span>
            <span style={{ display: 'block', fontSize: '12px', color: 'var(--ink-500)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{subheading}</span>
          </span>
          {!anonyme && (creation || f) && (
            <button type="button" onClick={() => void save()} aria-disabled={enregistrement || undefined}
              style={{ height: 40, padding: '0 18px', border: 'none', borderRadius: 'var(--r-sm)', background: 'var(--teal-500)', color: '#fff', fontSize: '13.5px', fontWeight: 600, flexShrink: 0, cursor: 'pointer', opacity: enregistrement ? .6 : 1 }}>
              {enregistrement ? t('Enregistrement…') : creation ? t('Créer le client') : t('Enregistrer')}
            </button>
          )}
          <button type="button" onClick={() => onDone(false)} aria-label={t('Fermer')}
            style={{ width: 34, height: 34, border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--ink-100)', color: 'var(--ink-600)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: 'pointer' }}>
            <Icon name="x" size={15} stroke={2.2} />
          </button>
        </div>

        <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
          {/* ── Le rail des onglets ──────────────────────────────────────────────────────────────── */}
          <div style={{ width: 186, padding: '14px 12px', borderRight: '1px solid var(--ink-200)', background: 'var(--ink-50)', display: 'flex', flexDirection: 'column', gap: 3, flexShrink: 0 }}>
            {onglets.map((o) => (
              <button aria-pressed={onglet === o.id} key={o.id} type="button" onClick={() => setOnglet(o.id)}
                style={{ display: 'flex', alignItems: 'center', gap: 9, height: 36, padding: '0 11px', border: 'none', borderRadius: 'var(--r-ctrl)', background: onglet === o.id ? 'var(--paper)' : 'transparent', color: onglet === o.id ? 'var(--teal-700)' : 'var(--ink-600)', fontSize: '13px', fontWeight: 600, textAlign: 'left', boxShadow: onglet === o.id ? '0 1px 3px rgba(12,14,16,.08)' : 'none', cursor: 'pointer' }}>
                <Icon name={o.icon} size={15} stroke={1.8} />
                {t(o.l)}
              </button>
            ))}
          </div>

          {/* ⚠️ L'HISTORIQUE NE FAIT PAS DÉFILER LA ZONE ENTIÈRE (décision utilisateur, comme en caisse) :
              borné à la hauteur de la modale, c'est son TABLEAU qui défile — filtre, tuiles et en-têtes
              restent en place. Les autres onglets défilent comme avant. */}
          <div style={onglet === 'hist'
            ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '20px 22px' }
            : { flex: 1, overflowY: 'auto', padding: '20px 22px' }}>
            {failure ? (
              <div role="alert" style={{ color: 'var(--danger-ink)', fontSize: '13px' }}>
                
                {t('La fiche n’a pas pu être lue :')} {failure}
                <button type="button" className="btn-ghost" onClick={() => void load()} style={{ marginLeft: 12 }}>{t('Réessayer')}</button>
              </div>
            ) : !creation && !detail ? (
              <p style={{ margin: 0, color: 'var(--ink-500)', fontSize: '13px' }}>{t('Lecture de la fiche…')}</p>
            ) : (
              <>
                {/* ══ GÉNÉRAL ══ */}
                {onglet === 'gen' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    {anonyme && (
                      <div style={noteGrise}><IconInfo /><span><Trans k='<1>Fiche anonymisée.</1> L’identité est détachée — ces champs ne se modifient plus.' parts={[(x) => <strong>{x}</strong>]} /></span></div>
                    )}
                    <div>
                      <div style={sectionHeading}>{t('Identité')}</div>
                      {/* ⚠️ La colonne civilité suit ses segments (trois codes, plus une valeur reprise hors
                          liste le cas échéant) : une largeur fixe rognait « Mme » dès le rembourrage. */}
                      <div style={{ display: 'grid', gridTemplateColumns: `${civilites.length > 2 ? 'auto' : '120px'} 1fr 1fr`, gap: 10 }}>
                        <span>
                          <span style={etiquette}>{t('Civilité')}</span>
                          <span style={{ display: 'flex', gap: 3, padding: 3, background: 'var(--ink-100)', borderRadius: 'var(--r-ctrl)', height: 38, boxSizing: 'border-box' }}>
                            {civilites.map((c) => (
                              <button key={c} type="button" onClick={() => setD((x) => ({ ...x, title: c }))} disabled={anonyme}
                                style={{ flex: 1, border: 'none', borderRadius: 'var(--r-xs)', padding: '0 8px', fontSize: '12.5px', fontWeight: 600, whiteSpace: 'nowrap', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', background: d.title === c ? 'var(--paper)' : 'transparent', color: d.title === c ? 'var(--ink-900)' : 'var(--ink-500)', boxShadow: d.title === c ? '0 1px 3px rgba(12,14,16,.1)' : 'none', cursor: 'pointer' }}>{isCivilityCode(c) ? t(CIVILITE_LIBELLES[c]) : c}</button>
                            ))}
                          </span>
                        </span>
                        <span><span id={`${fieldId}-0`} style={etiquette}>{t('Nom')}</span><input aria-labelledby={`${fieldId}-0`} value={d.lastName} onChange={bindField('lastName')} disabled={anonyme} style={{ ...champ, fontWeight: 600, textTransform: 'uppercase' }} /></span>
                        <span><span id={`${fieldId}-1`} style={etiquette}>{t('Prénom')}</span><input aria-labelledby={`${fieldId}-1`} value={d.firstName} onChange={bindField('firstName')} disabled={anonyme} style={champ} /></span>
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 22 }}>
                      <div>
                        <div style={sectionHeading}>{t('Coordonnées')}</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          <span><span id={`${fieldId}-2`} style={etiquette}>{t('Email')}</span><input aria-labelledby={`${fieldId}-2`} value={d.email} onChange={bindField('email')} disabled={anonyme} style={champ} /></span>
                          {/* ⚠️ Mobile ET Fixe (écart maquette assumé) : un champ unique enregistré dans l'un écraserait l'autre. */}
                          <span style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                            <span><span id={`${fieldId}-3`} style={etiquette}>{t('Mobile')}</span><input aria-labelledby={`${fieldId}-3`} value={d.mobilePhone} onChange={bindField('mobilePhone')} disabled={anonyme} style={{ ...champ, fontVariantNumeric: 'tabular-nums' }} /></span>
                            <span><span id={`${fieldId}-4`} style={etiquette}>{t('Fixe')}</span><input aria-labelledby={`${fieldId}-4`} value={d.landlinePhone} onChange={bindField('landlinePhone')} disabled={anonyme} style={{ ...champ, fontVariantNumeric: 'tabular-nums' }} /></span>
                          </span>
                          <span><span id={`${fieldId}-10`} style={etiquette}>{t('Naissance')}</span><input aria-labelledby={`${fieldId}-10`} value={d.birthdate} onChange={bindField('birthdate')} disabled={anonyme} placeholder={t('JJ/MM/AAAA')} style={{ ...champ, fontVariantNumeric: 'tabular-nums' }} /></span>
                          <span style={{ fontSize: '11px', color: 'var(--ink-400)' }}>{t('Saisie tolérante : 01011990, 01-01-1990, 01/01/1990…')}</span>
                        </div>
                      </div>
                      <div>
                        <div style={sectionHeading}>{t('Adresse')}</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          <span><span id={`${fieldId}-5`} style={etiquette}>{t('Rue')}</span><input aria-labelledby={`${fieldId}-5`} value={d.address1} onChange={bindField('address1')} disabled={anonyme} style={champ} /></span>
                          <CommuneField
                            postalCode={d.postalCode} city={d.city} disabled={anonyme}
                            onChange={(n) => setD((x) => ({ ...x, postalCode: n.postalCode, city: n.city }))}
                            labelStyle={etiquette} inputStyle={champ}
                          />
                        </div>
                      </div>
                    </div>
                    <div>
                      <div style={sectionHeading}>{t('Fidélité')}</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {/* ⚠️ LA CARTE EST LE N° CLIENT, AUTOMATIQUE, ET ELLE NE SE MODIFIE PAS — même par un
                            administrateur (décision utilisateur) : la réattribuer déplacerait les points, les
                            titres et l'historique d'une personne. */}
                        <span><span style={etiquette}>{t('Carte de fidélité')}</span>
                          <span style={{ display: 'block', height: 38, lineHeight: '38px', fontFamily: MONO, fontSize: '13px', color: creation ? 'var(--ink-500)' : 'var(--ink-900)' }}>
                            {creation ? t('Attribuée à l’enregistrement — c’est le n° client') : f?.loyaltyNumber ?? '—'}
                          </span>
                        </span>
                        {/* LA REMISE PERMANENTE (CADRAGE-Remise-Permanente.md) — un taux de la fiche, appliqué
                            d'office en caisse à chaque article remisable, quel que soit l'ordre des gestes. */}
                        <span><span id={`${fieldId}-11`} style={etiquette}>{t('Remise permanente')}</span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <input aria-labelledby={`${fieldId}-11`} value={d.permanentDiscount} onChange={bindField('permanentDiscount')} disabled={anonyme} inputMode="decimal" placeholder="0"
                              style={{ ...champ, width: 96, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }} />
                            <span style={{ fontSize: '13px', color: 'var(--ink-600)' }}>%</span>
                          </span>
                          <span style={{ display: 'block', marginTop: 4, fontSize: '11px', color: 'var(--ink-400)' }}>
                            {t('Appliquée d’office en caisse à chaque article remisable — jamais sur une promotion non cumulable, un titre ni un retour.')}
                          </span>
                        </span>
                        {/* ⚠️ LE MONTANT DES GAINS EST UN BOUTON — exactement comme en caisse : c'est LUI qui
                            ouvre le grand livre (il n'y a pas d'onglet Fidélité, le programme est global).
                            ⚠️ NÉGATIF : la carte CHANGE DE COULEUR et se coiffe de son motif (audit F22). Une
                            cagnotte négative peinte en vert « gain disponible » dirait le contraire du vrai. */}
                        {!creation && detail && (
                          <Tooltip display="block" label={isNegative ? t('Cagnotte négative') : undefined} content={balanceReason ?? undefined}>
                            <button type="button" onClick={() => setCagnotte(true)}
                              style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', padding: 14, border: 'none', borderRadius: 'var(--r-md)', background: isNegative ? 'var(--danger-bg)' : 'var(--teal-100)', cursor: 'pointer' }}>
                              <Icon name="star" size={20} stroke={2} color={isNegative ? 'var(--danger-ink)' : 'var(--teal-700)'} />
                              <span style={{ flex: 1 }}>
                                <span style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: isNegative ? 'var(--danger-ink)' : 'var(--teal-800)' }}>{isNegative ? t('Cagnotte négative') : t('Gain disponible')}</span>
                                <span style={{ display: 'block', fontSize: '11.5px', color: isNegative ? 'var(--danger-ink)' : 'var(--teal-700)' }}>{t('Cliquer pour le détail de la cagnotte')}</span>
                              </span>
                              <span style={{ fontSize: '20px', fontWeight: 700, color: isNegative ? 'var(--danger-ink)' : 'var(--teal-800)', fontVariantNumeric: 'tabular-nums' }}>{euros(detail.record.loyaltyCents)}</span>
                              <Icon name="chevronR" size={16} stroke={2} color={isNegative ? 'var(--danger-ink)' : 'var(--teal-700)'} />
                            </button>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                    <div>
                      <div style={sectionHeading}>{t('Préférences & consentements')}</div>
                      {/* ⚠️ OPT-IN POSITIF (écart maquette assumé, §6.4 RGPD) : l'absence de refus n'est pas un
                          consentement. Cocher = consentir, et le serveur horodate qui l'a enregistré. */}
                      {/* ⚠️ DES INTERRUPTEURS, PAS DES CASES (demande utilisateur 2026-09-06) : une case
                          se coche sans qu'on la voie changer d'état à distance, et le geste au comptoir
                          se fait devant le client. L'interrupteur de la charte porte un vrai
                          `role="switch"` et une cible à la taille du doigt.
                          ⚠️ ACTIFS PAR DÉFAUT sur une fiche NEUVE, comme en caisse (décision utilisateur
                          2026-08-30, reprise ici) : le vendeur DÉCOCHE si le client refuse. La question
                          est posée de vive voix — le réglage n'est qu'un enregistrement de la réponse. */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                        <Switch checked={d.optInEmail} onChange={(v) => setD((x) => ({ ...x, optInEmail: v }))}
                          disabled={anonyme} layout="card" label={t('Accepte les emails')}
                          hint={t('Reçus, offres et informations du magasin')} />
                        <Switch checked={d.optInSms} onChange={(v) => setD((x) => ({ ...x, optInSms: v }))}
                          disabled={anonyme} layout="card" label={t('Accepte les SMS')}
                          hint={t('Envois courts — un mobile au dossier est nécessaire')} />
                        <Switch checked={d.isWeb} onChange={(v) => setD((x) => ({ ...x, isWeb: v }))}
                          disabled={anonyme} layout="card" label={t('Client web')}
                          hint={t('Compte créé ou utilisé sur la boutique en ligne')} />
                        <Switch checked={d.isPro} onChange={(v) => setD((x) => ({ ...x, isPro: v }))}
                          disabled={anonyme} layout="card" label={t('Client professionnel')}
                          hint={t('Révèle le feuillet Entreprise (SIRET, TVA, NAF)')} />
                      </div>
                    </div>
                    {d.isPro && (
                      <div>
                        <div style={sectionHeading}>{t('Entreprise')}</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          <span><span id={`${fieldId}-6`} style={etiquette}>{t('Raison sociale')}</span><input aria-labelledby={`${fieldId}-6`} value={d.companyLegalName} onChange={bindField('companyLegalName')} disabled={anonyme} style={{ ...champ, textTransform: 'uppercase' }} /></span>
                          <span style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 110px', gap: 10 }}>
                            <span><span id={`${fieldId}-7`} style={etiquette}>SIRET</span><input aria-labelledby={`${fieldId}-7`} value={d.siret} onChange={bindField('siret')} disabled={anonyme} style={{ ...champ, fontFamily: MONO, fontSize: '12.5px' }} /></span>
                            <span><span id={`${fieldId}-8`} style={etiquette}>{t('TVA intracommunautaire')}</span><input aria-labelledby={`${fieldId}-8`} value={d.vatNumber} onChange={bindField('vatNumber')} disabled={anonyme} style={{ ...champ, fontFamily: MONO, fontSize: '12.5px', textTransform: 'uppercase' }} /></span>
                            <span><span id={`${fieldId}-9`} style={etiquette}>NAF</span><input aria-labelledby={`${fieldId}-9`} value={d.naf} onChange={bindField('naf')} disabled={anonyme} style={{ ...champ, fontFamily: MONO, fontSize: '12.5px', textTransform: 'uppercase' }} /></span>
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ══ WALLET — le consentement (écrit avec la fiche) et l'état RÉEL du pass ══ */}
                {/* ══ QUALIFICATIONS ══ */}
                {/* Porté de la maquette tablette (fiche client, onglet « Qualifications ») : DEUX zones — ce que
                    le client porte, puis ce que le magasin propose — et une pastille à POINT COLORÉ par étiquette.
                    ⚠️ La couleur vient de `reference_list.color`, choisie par le magasin. Sans couleur réglée, ton
                    neutre : on n'en invente pas une, elle passerait pour un choix. */}
                {onglet === 'qualif' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
                    {anonyme ? (
                      <div style={noteGrise}>
                        <IconInfo />
                        <span><Trans k='Une fiche <1>anonymisée</1> ne porte plus de qualification : elle ne qualifie plus personne.' parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} /></span>
                      </div>
                    ) : (
                      <>
                        <div>
                          <div style={sectionHeading}>{t('Qualifications du client')}</div>
                          {posees.length === 0 ? (
                            <p style={{ margin: 0, fontSize: '13.5px', color: 'var(--ink-500)' }}>{t('Aucune qualification pour ce client.')}</p>
                          ) : (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                              {posees.map((o) => (
                                <span key={o.code} style={{ ...pastilleQualif(o.color), paddingRight: 6 }}>
                                  <PointQualif color={o.color} />
                                  {o.label}
                                  {/* Retirer est aussi direct que poser — sinon l'étiquette de trop reste. */}
                                  <button type="button" aria-label={t('Retirer {label}', { label: o.label })}
                                    onClick={() => setD((x) => ({ ...x, qualifications: x.qualifications.filter((c) => c !== o.code) }))}
                                    style={{ width: 20, height: 20, marginLeft: 2, border: 'none', borderRadius: '50%', background: 'transparent', color: 'var(--ink-500)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Icon name="x" size={11} stroke={2.6} />
                                  </button>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>

                        <div>
                          <div style={sectionHeading}>{t('Liste du magasin')}</div>
                          {aProposer.length === 0 ? (
                            <p style={{ margin: 0, fontSize: '13.5px', color: 'var(--ink-500)' }}>
                              {optionsQualif.length === 0
                                ? t('Aucune étiquette n’est définie pour ce magasin.')
                                : t('Toutes les étiquettes du magasin sont déjà posées sur cette fiche.')}
                            </p>
                          ) : (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                              {aProposer.map((o) => (
                                <button key={o.code} type="button"
                                  onClick={() => setD((x) => ({ ...x, qualifications: [...x.qualifications, o.code] }))}
                                  style={{ ...pastilleQualif(o.color), cursor: 'pointer' }}>
                                  <PointQualif color={o.color} />
                                  {o.label}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>

                        <div>
                          <div style={sectionHeading}>{t('Ajouter une étiquette')}</div>
                          {/* Créer DEPUIS la fiche : la liste se construit à l'usage, sans détour obligé par l'écran
                              Référentiels — et l'échec se dit (la pastille n'apparaît pas). */}
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                            <input type="text" value={nouvelleQualif} onChange={(e) => setNouvelleQualif(e.target.value)}
                              placeholder={t('Ex. Donateur, Acheteur, Vendeur…')} aria-label={t('Nouvelle qualification')}
                              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void creerQualif(); } }}
                              style={{ flex: '1 1 220px', minWidth: 0, height: 38, padding: '0 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: '13.5px' }} />
                            <button type="button" className="btn-ghost" onClick={() => void creerQualif()} aria-disabled={!nouvelleQualif.trim() || undefined}>
                              
                              {t('Créer et poser')}
                            </button>
                          </div>
                        </div>

                        <div style={noteGrise}>
                          <IconInfo />
                          <span>
                            <Trans k='Étiquettes <1>libres, propres au magasin</1> (ex. Donateur, Acheteur, Vendeur). La liste et ses couleurs se paramètrent dans <2>Réglages › Référentiels › Qualifications clients</2> ; une modification s’applique immédiatement. Une étiquette <3>désactivée</3> reste affichée si la fiche la porte — la retirer du choix ne détache aucune fiche.' parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} /></span>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {onglet === 'wallet' && detail && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 14, padding: 18, borderRadius: 'var(--r-md)',
                      background: d.optInWallet ? 'var(--teal-100)' : 'var(--ink-50)',
                      border: `1px solid ${d.optInWallet ? 'var(--teal-300)' : 'var(--ink-200)'}`,
                    }}>
                      <span style={{ width: 44, height: 44, borderRadius: 'var(--r-ctrl)', background: d.optInWallet ? 'var(--teal-500)' : 'var(--ink-200)', color: d.optInWallet ? '#fff' : 'var(--ink-500)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Icon name="wallet" size={22} stroke={1.9} />
                      </span>
                      <span style={{ flex: 1 }}>
                        <span style={{ display: 'block', fontSize: '15px', fontWeight: 700, color: d.optInWallet ? 'var(--teal-800)' : 'var(--ink-700)' }}>
                          {/* ⚠️ SANS CLÉ, LE TITRE DIT « NON ACTIVÉ » (demande utilisateur 2026-09-06) :
                              « pas de carte dématérialisée » se lit comme un choix du client, alors que
                              c'est le MAGASIN qui n'a pas encore de connecteur. Deux causes opposées ne
                              peuvent pas porter le même titre. Même formulation en caisse. */}
                          {walletPret === false || !walletActif
                            ? t('Wallet non activé')
                            : passEnCours
                              ? (d.optInWallet ? t('Désactivation…') : t('Création de la carte…'))
                              : d.optInWallet ? t('Carte dématérialisée active') : t('Pas de carte dématérialisée')}
                        </span>
                        <span style={{ display: 'block', fontSize: '12px', color: d.optInWallet ? 'var(--teal-700)' : 'var(--ink-500)', lineHeight: 1.4 }}>
                          {/* ⚠️ DEUX REFUS DISTINCTS, DEUX PHRASES : « pas de clé » se corrige sur la
                              carte Connexion du connecteur, « programme suspendu » sur la carte de ses réglages
                              (les réglages wallet vivent dans l'intégration, jamais dans Paramètres). Les
                              confondre enverrait corriger la mauvaise chose. */}
                          {walletPret === null
                            ? t('Lecture du connecteur wallet du magasin…')
                            : !walletActif
                              ? <><Trans k='Le programme est <1>désactivé pour ce magasin</1> — à réactiver dans <2>Intégrations → Wallet</2>, carte « Ce que la caisse fait du portefeuille ». La clé, elle, reste en place.' parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} /></>
                              : walletPret === false
                                ? <><Trans k='Aucune clé API wallet pour ce magasin — à renseigner dans <1>Intégrations → Wallet</1>.' parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} /></>
                                : d.optInWallet
                                  ? t('La carte existe chez Eldowallet et reçoit les gains, coupons et notifications.')
                                  : t('Activez pour créer la carte du client : Eldowallet lui enverra lui-même le lien.')}
                        </span>
                      </span>
                      {/* ⚠️ L'INTERRUPTEUR PARTAGÉ, pas une case native : même géométrie que la
                          maquette et que la caisse, cible à la taille du doigt, et vrai
                          `role="switch"` (une case de 13 px sur un choix qui engage se rate sans
                          que rien ne le dise). */}
                      {/* ⚠️ L'INTERRUPTEUR EST LA CARTE : l'activer crée le pass chez Eldowallet en
                          un seul geste. Verrouillé pendant l'appel (le travail se voit), inerte sans
                          clé API (on ne propose pas ce que le magasin ne peut pas faire), et il ne
                          bascule qu'APRÈS la réponse du serveur — jamais sur l'intention. */}
                      <Switch
                        checked={d.optInWallet}
                        onChange={(v) => { void basculerWallet(v); }}
                        label=""
                        disabled={anonyme || passEnCours || walletPret !== true}
                      />
                    </div>

                    {/* ⚠️ L'ÉTAT DU PASS EST LU, JAMAIS SUPPOSÉ : un consentement n'est pas un pass émis. */}
                    <div style={{ padding: 16, borderRadius: 'var(--r-sm)', background: 'var(--ink-50)', border: '1px solid var(--ink-200)', display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--ink-700)' }}>
                        {detail.wallet.hasPass
                          ? (detail.wallet.passActive
                            ? t('Pass {v} — actif', { v: detail.wallet.platform ?? t('émis') })
                            : t('Pass {v} — désactivé', { v: detail.wallet.platform ?? t('émis') }))
                          : t('Aucun pass émis pour cette fiche')}
                      </div>
                      <div style={{ fontSize: '12.5px', color: 'var(--ink-600)', lineHeight: 1.5 }}>
                        {detail.wallet.hasPass
                          ? t('La carte existe chez Eldowallet et porte l’identité de cette fiche : c’est elle qui reçoit les gains, les coupons et les notifications.')
                          : t('Il faut une adresse e-mail et un numéro de carte : Eldowallet exige la première, et le second relie la carte au client.')}
                      </div>
                    </div>
                    <div style={noteGrise}><IconInfo /><span><Trans k='C’est <1>Eldowallet</1> qui envoie lui-même le lien du pass au client (SMS/email de son service). Nos propres envois — marketing, tickets en ligne — sont un canal distinct.' parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} /></span></div>
                  </div>
                )}

                {/* ══ HISTORIQUE ══ */}
                {onglet === 'hist' && detail && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12, flex: 1, minHeight: 0 }}>
                    {/* ⚠️ MÊME BARRE QU'EN CAISSE : le filtre de période et le raccourci vers le grand
                        livre sur UNE ligne, puis les tuiles. Deux écrans qui montrent le même
                        historique au même client ne peuvent pas le résumer autrement. */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      <span style={{ display: 'flex', gap: 3, padding: 3, background: 'var(--ink-100)', borderRadius: 'var(--r-ctrl)' }}>
                        {([['all', t('Tout')], ['month', t('Ce mois')], ['year', t('Cette année')]] as const).map(([id, l]) => (
                          <button aria-pressed={periode === id} key={id} type="button" onClick={() => setPeriode(id)}
                            style={{ border: 'none', borderRadius: 'var(--r-xs)', padding: '7px 13px', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', background: periode === id ? 'var(--paper)' : 'transparent', color: periode === id ? 'var(--ink-900)' : 'var(--ink-500)', boxShadow: periode === id ? '0 1px 3px rgba(12,14,16,.1)' : 'none' }}>{l}</button>
                        ))}
                      </span>
                      <span style={{ flex: 1 }} />
                      {/* Le 2ᵉ point d'entrée du grand livre, comme en caisse (« Gains accumulés »). */}
                      <button type="button" className="btn-ghost" onClick={() => setCagnotte(true)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <Icon name="star" size={14} stroke={1.9} />
                        
                        {t('Gains accumulés')}
                      </button>
                    </div>
                    {/* Les TROIS tuiles de la caisse, sur la période choisie.
                        ⚠️ UN COMPTE ET SON UNITÉ SONT UNE SEULE PHRASE COMPTÉE : rendus séparément, ils affichaient
                        « 1 tickets » — et « tickets » n'était même pas traduit. Le montant, lui, porte déjà sa devise. */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                      {([
                        [t('Achats sur la période'), <Trans k='<1>{n}</1> <2>ticket</2>' vars={{ n: new Set(histVisible.map((l) => l.pieceNumber)).size }} parts={[TILE_FIGURE, TILE_UNIT]} />],
                        [t('Articles achetés'), <Trans k='<1>{n}</1> <2>pièce</2>' vars={{ n: histVisible.reduce((n2, l) => n2 + l.qty, 0) }} parts={[TILE_FIGURE, TILE_UNIT]} />],
                        [t('Total dépensé'), TILE_FIGURE(euros(histVisible.reduce((n2, l) => n2 + l.totalCents, 0)))],
                      ] as const).map(([l, value]) => (
                        <div key={l} style={{ padding: 14, borderRadius: 'var(--r-md)', background: 'var(--ink-50)' }}>
                          <div style={{ fontSize: '11px', color: 'var(--ink-500)', textTransform: 'uppercase', fontWeight: 600 }}>{l}</div>
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 4 }}>{value}</div>
                        </div>
                      ))}
                    </div>
                    {histVisible.length === 0 ? (
                      <div style={{ padding: '40px 20px', textAlign: 'center', border: '1px dashed var(--ink-200)', borderRadius: 'var(--r-sm)' }}>
                        <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--ink-700)' }}>{t('Aucun achat pour l’instant')}</div>
                        <div style={{ fontSize: '12px', color: 'var(--ink-500)', marginTop: 3 }}>{t('L’historique se remplit au fil des ventes rattachées à la fiche.')}</div>
                      </div>
                    ) : (
                      <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                        {/* ⚠️ MÊME ORDRE QU'EN CAISSE (2026-09-06) : article, taille, quantité, prix. Le
                            n° de pièce rejoint la ligne de l'article — il y reste cliquable, et la
                            colonne qu'il occupait profite à la désignation. */}
                        <div style={{ display: 'grid', gridTemplateColumns: '88px 1fr 62px 48px 92px', gap: 8, padding: '9px 14px', background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-200)', fontSize: '10.5px', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>
                          <ThHist label={t('Date')} k="at" tri={triHist} onTri={setTriHist} />
                          <ThHist label={t('Article')} k="label" tri={triHist} onTri={setTriHist} />
                          <ThHist label={fieldLabel('size')} k="size" tri={triHist} onTri={setTriHist} />
                          <ThHist label={t('Qté')} k="qty" align="center" tri={triHist} onTri={setTriHist} />
                          <ThHist label={t('Prix')} k="total" align="right" tri={triHist} onTri={setTriHist} />
                        </div>
                        <div data-testid="historique-defilant" style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
                        {histVisible.map((l, i) => (
                          <div key={i} style={{ display: 'grid', gridTemplateColumns: '88px 1fr 62px 48px 92px', gap: 8, alignItems: 'center', padding: '8px 14px', borderBottom: '1px solid var(--ink-100)' }}>
                            <span style={{ fontSize: '11.5px', color: 'var(--ink-500)', fontVariantNumeric: 'tabular-nums' }}>{dateFiscale(l.at)}</span>
                            <span style={{ minWidth: 0 }}>
                              <span style={{ display: 'block', fontSize: '12.5px', fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.label}</span>
                              {/* ⚠️ LE CODE ARTICLE ET LE COLORIS, comme en caisse : la désignation seule ne
                                  suffit pas à retrouver la bonne déclinaison. ⚠️ LA PIÈCE S'OUVRE toujours,
                                  PAR IDENTIFIANT — le journal ne s'ouvre jamais par numéro. */}
                              <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 1 }}>
                                {!!l.brand && <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--teal-700)' }}>{l.brand}</span>}
                                {!!l.articleCode && <span style={{ fontSize: '11px', color: 'var(--ink-500)', fontFamily: MONO }}>{l.articleCode}</span>}
                                {!!l.color && <span style={{ fontSize: '11px', color: 'var(--ink-500)' }}>· {l.color}</span>}
                                {l.pieceId ? (
                                  <button type="button" title={t('Ouvrir cette vente dans le journal')}
                                    onClick={() => { onDone(false); navigate(`/journal?open=${encodeURIComponent(l.pieceId)}`); }}
                                    style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', fontFamily: MONO, fontSize: '11px', color: 'var(--teal-700)', textDecoration: 'underline' }}>
                                    · {l.pieceNumber}
                                  </button>
                                ) : (
                                  <span style={{ fontFamily: MONO, fontSize: '11px', color: 'var(--ink-400)' }}>· {l.pieceNumber}</span>
                                )}
                              </span>
                            </span>
                            <span style={{ fontSize: '12px', color: 'var(--ink-600)' }}>{l.size ?? '—'}</span>
                            <span style={{ textAlign: 'center', fontSize: '12.5px', color: l.qty < 0 ? 'var(--rose-fg)' : 'var(--ink-700)', fontVariantNumeric: 'tabular-nums' }}>{l.qty}</span>
                            <span style={{ textAlign: 'right', fontSize: '12.5px', fontWeight: 700, color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>{euros(l.totalCents)}</span>
                          </div>
                        ))}
                        </div>
                        {detail.historyTruncated && (
                          <div style={{ padding: '9px 14px', fontSize: '11.5px', color: 'var(--ink-500)', background: 'var(--ink-50)' }}>{t('Les 200 lignes les plus récentes sont affichées — l’historique complet reste en base.')}</div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* ══ AVOIRS (titres et bons) ══ */}
                {onglet === 'av' && detail && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {/* ⚠️ MÊMES GESTES QU'EN CAISSE : chercher un n°, filtrer par type, trier. Les puces
                        viennent des types RÉELLEMENT présents — en proposer un que ce client n'a pas
                        offrirait un geste qui ne mène nulle part (§23.0). */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <input value={rechBon} onChange={(e) => setRechBon(e.target.value)} placeholder={t('Rechercher un n° de bon…')}
                        style={{ ...champ, width: 210 }} />
                      <span style={{ display: 'flex', gap: 3, padding: 3, background: 'var(--ink-100)', borderRadius: 'var(--r-ctrl)' }}>
                        {[{ v: 'all', l: t('Tous') }, ...typesBonsPresents.map((x) => ({ v: x, l: typeUi(x).l }))].map((f) => (
                          <button aria-pressed={filtreBon === f.v} key={f.v} type="button" onClick={() => setFiltreBon(f.v)}
                            style={{ border: 'none', borderRadius: 'var(--r-xs)', padding: '7px 12px', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', background: filtreBon === f.v ? 'var(--paper)' : 'transparent', color: filtreBon === f.v ? 'var(--ink-900)' : 'var(--ink-500)', boxShadow: filtreBon === f.v ? '0 1px 3px rgba(12,14,16,.1)' : 'none' }}>{f.l}</button>
                        ))}
                      </span>
                      <span style={{ flex: 1 }} />
                      <span style={{ fontSize: '11px', color: 'var(--ink-500)' }}>{t('Trier par')}</span>
                      <span style={{ display: 'flex', gap: 3, padding: 3, background: 'var(--ink-100)', borderRadius: 'var(--r-ctrl)' }}>
                        {([['code', 'N°'], ['issued', t('Date')], ['expires', t('Échéance')], ['state', t('Statut')], ['amount', t('Montant')]] as const).map(([k, l]) => (
                          <button key={k} type="button"
                            onClick={() => setTriBon((t2) => (t2.key === k ? { key: k, dir: t2.dir === 'asc' ? 'desc' : 'asc' } : { key: k, dir: k === 'code' ? 'asc' : 'desc' }))}
                            style={{ border: 'none', borderRadius: 'var(--r-xs)', padding: '7px 11px', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', background: triBon.key === k ? 'var(--paper)' : 'transparent', color: triBon.key === k ? 'var(--ink-900)' : 'var(--ink-500)', boxShadow: triBon.key === k ? '0 1px 3px rgba(12,14,16,.1)' : 'none' }}>
                            {l}{triBon.key === k ? (triBon.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                          </button>
                        ))}
                      </span>
                    </div>
                    {bonsVisibles.map((v) => {
                      const ui = typeUi(v.type);
                      return (
                        <div key={v.code + String(v.issuedAt)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', background: 'var(--paper)' }}>
                          <span style={{ width: 36, height: 36, borderRadius: 'var(--r-ctrl)', background: ui.bg, color: ui.fg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Icon name={ui.icon} size={17} stroke={1.8} />
                          </span>
                          <span style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--ink-900)' }}>{ui.l}</span>
                              <span style={{ fontFamily: MONO, fontSize: '11px', color: 'var(--ink-500)' }}>{v.code}</span>
                            </span>
                            <span style={{ display: 'block', fontSize: '11.5px', color: 'var(--ink-500)', marginTop: 2 }}>
                              
                              {v.expiresAt
                                ? t('Émis le {v} · valable jusqu’au {v2} — durée figée au jour d’émission', { v: dateEpoch(v.issuedAt), v2: dateEpoch(v.expiresAt) })
                                : t('Émis le {v}', { v: dateEpoch(v.issuedAt) })}
                              {v.state === 'partial' ? <>{' · '}{t('reste {v}', { v: euros(v.remainingCents) })}</> : ''}
                            </span>
                          </span>
{/* ⚠️ STATUT PUIS MONTANT (demande utilisateur 2026-09-06) : on regarde d'abord si le bon est
                              utilisable, le montant ne compte qu'ensuite. La caisse range dans le même ordre. */}
                          <Badge tone={ETAT_TITRE[v.state].tone} size="sm">{ETAT_TITRE[v.state].l}</Badge>
                          <span style={{ width: 96, textAlign: 'right', fontSize: '15px', fontWeight: 800, color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>{euros(v.amountCents)}</span>
                        </div>
                      );
                    })}
                    {bonsVisibles.length === 0 && (
                      <div style={{ padding: '40px 20px', textAlign: 'center', border: '1px dashed var(--ink-200)', borderRadius: 'var(--r-sm)' }}>
                        <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--ink-700)' }}>{t('Aucun avoir ni chèque cadeau')}</div>
                        <div style={{ fontSize: '12px', color: 'var(--ink-500)', marginTop: 3 }}>{t('Les avoirs, chèques cadeaux et acomptes émis pour ce client apparaîtront ici, passe-partout en caisse.')}</div>
                      </div>
                    )}
                    {bonsVisibles.length > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 'var(--r-ctrl)', background: 'var(--teal-100)', color: 'var(--teal-800)', fontSize: '12.5px', fontWeight: 600 }}>
                        <span style={{ flex: 1 }}>{t('Valeur des titres non remis')}</span>
                        <span style={{ fontSize: '14px', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
                          {/* ⚠️ LA RÈGLE DU CŒUR (`outstandingVoucherCents`), la même qu'en caisse : PÉRIMÉS
                              COMPRIS, consommés exclus — et sur TOUS les titres du client, un filtre de type
                              ne change pas ce qu'il détient. */}
                          {euros(outstandingVoucherCents(detail.vouchers))}
                        </span>
                      </div>
                    )}
                    <div style={noteGrise}><IconInfo /><span>{t('Payé → titre (avoir, chèque cadeau, acompte) · offert → remise (bon fidélité, coupon). Registre unifié, reporting émis / utilisés / en cours dans Titres & bons.')}</span></div>
                  </div>
                )}

                {/* ══ STATISTIQUES ══ */}
                {onglet === 'stats' && detail && stats && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                    {/* ⚠️ LES TUILES DE LA CAISSE, À LA LETTRE (demande utilisateur 2026-09-06) : pastille
                        d'icône teintée, valeur, puis libellé. L'écran de comptoir est la référence — deux
                        surfaces qui résument le même client ne peuvent pas le résumer autrement.
                        ⚠️ « Visites » et non « tickets » : c'est le mot de la caisse, et les deux comptent
                        la même chose (une pièce qui compte = un passage, remboursement compris). */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                      {([
                        [t('Total achats'), euros(stats.caCents), 'var(--teal-600)', 'bag'],
                        [t('Visites'), String(stats.tickets), '#248FCC', 'users'],
                        [t('Panier moyen'), euros(panierMoyen), '#C8741D', 'receipt'],
                        // ⚠️ Le libellé SUIT le signe (audit F22) : « Gain fidélité : −4,20 € » se lit comme un
                        //    gain de moins quatre euros. Ce n'en est pas un — c'est une avance à rattraper.
                        [isNegative ? t('Cagnotte négative') : t('Gain fidélité'), euros(detail.record.loyaltyCents), '#8677CE', 'star'],
                      ] as const).map(([l, v, couleur, ic]) => (
                        <div key={l} style={{ padding: 16, border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', background: 'var(--paper)' }}>
                          <span style={{ display: 'flex', width: 34, height: 34, borderRadius: 9, alignItems: 'center', justifyContent: 'center', marginBottom: 10, background: `color-mix(in oklab, ${couleur} 14%, transparent)`, color: couleur }}>
                            <Icon name={ic} size={18} />
                          </span>
                          <div style={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.4px', color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>{v}</div>
                          <div style={{ fontSize: '11px', color: 'var(--ink-500)', marginTop: 2 }}>{l}</div>
                        </div>
                      ))}
                    </div>

                    <div style={carteStat}>
                      <div style={titreStat}>{t('Dépenses sur 12 mois')}</div>
                      <div style={sousTitreStat}>{t('Montant dépensé par mois ({code})', { code: currentCurrency().code })}</div>
                      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 130, padding: '0 2px' }}>
                        {/* ⚠️ L'INFOBULLE DE LA CHARTE, PLUS L'INFOBULLE DU NAVIGATEUR (demande utilisateur
                            2026-09-06). `title` n'existe pas au clavier, met une seconde à venir et ne se
                            style pas ; `Tooltip` répond au survol ET au focus, et porte
                            `aria-describedby` — on ne troque pas de l'information contre de l'esthétique. */}
                        {detail.monthlySpend.map((m) => (
                          <Tooltip key={m.month} display="block" label={moisCourt(m.month)} content={euros(m.totalCents)}
                            style={{ flex: 1, height: '100%' }}>
                            <span style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, height: '100%', justifyContent: 'flex-end' }}>
                              <span style={{ width: '100%', height: `${rescale(pctBp(m.totalCents, maxMois), 2, 0)}%`, minHeight: m.totalCents > 0 ? 3 : 0, background: 'var(--teal-500)', borderRadius: '4px 4px 0 0' }} />
                              <span style={{ fontSize: '9.5px', color: 'var(--ink-400)', whiteSpace: 'nowrap' }}>{moisCourt(m.month)}</span>
                            </span>
                          </Tooltip>
                        ))}
                      </div>
                    </div>

                    <div style={carteStat}>
                      <div style={titreStat}>{t('Répartition par famille')}</div>
                      <div style={sousTitreStat}>{t('Sur l’ensemble des achats')}</div>
                      {totalFamilles === 0 ? (
                        <div style={{ fontSize: '12.5px', color: 'var(--ink-500)' }}>{t('Aucun article vendu à ce client — rien à répartir.')}</div>
                      ) : (
                        <>
                          <div style={{ display: 'flex', height: 14, borderRadius: 'var(--r-pill)', overflow: 'hidden', gap: 2 }}>
                            {detail.familyBreakdown.map((fam, i) => (
                              <Tooltip key={`${fam.kind}:${fam.family}`} display="block" label={familyName(fam)}
                                content={`${euros(fam.totalCents)} · ${rescale(pctBp(fam.totalCents, totalFamilles), 2, 0)} %`}
                                style={{ width: `${pctBp(fam.totalCents, totalFamilles) / 100}%` }}>
                                <span style={{ display: 'block', width: '100%', height: '100%', background: COULEURS_FAMILLE[i % COULEURS_FAMILLE.length] }} />
                              </Tooltip>
                            ))}
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 18px', marginTop: 12 }}>
                            {detail.familyBreakdown.map((fam, i) => (
                              <span key={`${fam.kind}:${fam.family}`} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '12.5px' }}>
                                <span style={{ width: 10, height: 10, borderRadius: 'var(--r-r3)', background: COULEURS_FAMILLE[i % COULEURS_FAMILLE.length], flexShrink: 0 }} />
                                <span style={{ flex: 1, color: 'var(--ink-700)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{familyName(fam)}</span>
                                <span style={{ color: 'var(--ink-900)', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{euros(fam.totalCents)}</span>
                                <span style={{ color: 'var(--ink-400)', fontVariantNumeric: 'tabular-nums', width: 34, textAlign: 'right' }}>{rescale(pctBp(fam.totalCents, totalFamilles), 2, 0)} %</span>
                              </span>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                    <div style={noteGrise}><IconInfo /><span><Trans k='Chiffre <1>net</1> des pièces rattachées à cette fiche : un retour le diminue et compte comme une visite, une vente annulée n’y figure pas. La même règle que la liste et la caisse.' parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} /></span></div>
                  </div>
                )}

                {/* ══ RGPD — propre au back-office ══ */}
                {onglet === 'rgpd' && detail && f && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', overflow: 'hidden' }}>
                      <div style={{ padding: '10px 14px', background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-200)', fontSize: '11px', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>{t('Consentements par canal')}</div>
                      {([
                        [t('Email'), f.optInEmail, f.email ? '' : t('aucune adresse au dossier')],
                        ['SMS', f.optInSms, f.mobilePhone ? '' : t('aucun mobile au dossier')],
                        // ⚠️ DES PHRASES ENTIÈRES, jamais un mot glissé comme valeur (Forme DS) : « actif » voyageait
                        //    en français dans une phrase traduite. Le pass non émis PARTIRA — mais la promesse ne
                        //    s'affiche que si elle est VRAIE : sans clé, ou programme suspendu, aucun cycle ne
                        //    l'émettra (§23.0) ; tant que l'état n'est pas lu, la ligne ne promet rien.
                        [t('Wallet'), f.optInWallet, detail.wallet.hasPass
                          ? (detail.wallet.platform
                            ? (detail.wallet.passActive ? t('pass {v} actif', { v: detail.wallet.platform }) : t('pass {v} désactivé', { v: detail.wallet.platform }))
                            : (detail.wallet.passActive ? t('pass actif') : t('pass désactivé')))
                          : f.optInWallet
                            ? {
                              next_cycle: t('pass pas encore émis — il partira au prochain cycle'),
                              not_activated: t('pass pas encore émis — wallet non activé pour ce magasin'),
                              unknown: t('pass pas encore émis'),
                            }[pendingPassNote(walletPret)]
                            : t('jamais consenti')],
                      ] as const).map(([canal, on, note]) => (
                        <div key={canal} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: '1px solid var(--ink-100)' }}>
                          <span style={{ width: 64, fontSize: '12.5px', fontWeight: 700, color: 'var(--ink-800)' }}>{canal}</span>
                          <span style={{ height: 20, padding: '0 9px', borderRadius: 'var(--r-pill)', background: on ? 'var(--teal-100)' : 'var(--ink-100)', color: on ? 'var(--teal-800)' : 'var(--ink-500)', fontSize: '10.5px', fontWeight: 700, display: 'inline-flex', alignItems: 'center' }}>{on ? t('Opt-in') : t('Opt-out')}</span>
                          <span style={{ flex: 1, fontSize: '11.5px', color: 'var(--ink-500)', textAlign: 'right' }}>{note}</span>
                        </div>
                      ))}
                      {/* ⚠️ UN SEUL horodatage au schéma (consent_at/channel/by) — l'écran dit CE QU'IL TIENT. */}
                      <div style={{ padding: '9px 14px', fontSize: '11px', color: 'var(--ink-400)' }}>
                        {f.consentAt
                          ? t('Dernier consentement enregistré le {v} · {v2} · par {v3}.', { v: dateEpoch(f.consentAt), v2: f.consentChannel ?? '—', v3: f.consentBy ?? '—' })
                          : t('Aucun horodatage de consentement enregistré.')}
                        {' '}
                        
                        {t('Opt-out centralisé : un STOP SMS ou une désinscription email coupe le canal partout, immédiatement.')}
                      </div>
                    </div>
                    {/* ⚠️ LA DEMANDE DU COMPTOIR S'AFFICHE LÀ OÙ ON AGIT (2026-09-06). La caisse peut
                        RECEVOIR une demande d'effacement — le client y est physiquement — mais elle
                        n'anonymise pas : c'est irréversible, et le comptoir avec une file d'attente
                        derrière est le pire endroit pour un acte sans retour. Elle remonte donc ici,
                        juste au-dessus du bouton qui l'exécute. */}
                    {!anonyme && detail.erasureRequest && (
                      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '12px 14px', borderRadius: 'var(--r-ctrl)', background: 'var(--amber-bg)', color: 'var(--amber-fg)', fontSize: '12.5px', lineHeight: 1.55 }}>
                        <IconInfo />
                        <span>
                          {/* ⚠️ UNE PHRASE ENTIÈRE PAR CAS : « le » et « par » vivaient hors du traduit. */}
                          {detail.erasureRequest.requestedBy
                            ? <Trans k='<1>Demande d’effacement reçue en caisse</1> le {v} par {v2}.' vars={{ v: dateEpoch(detail.erasureRequest.requestedAt), v2: detail.erasureRequest.requestedBy }} parts={[(x) => <strong>{x}</strong>]} />
                            : <Trans k='<1>Demande d’effacement reçue en caisse</1> le {v}.' vars={{ v: dateEpoch(detail.erasureRequest.requestedAt) }} parts={[(x) => <strong>{x}</strong>]} />}
                          {detail.erasureRequest.note ? ` « ${detail.erasureRequest.note} »` : ''}
                          {' '}{t('Anonymiser la fiche solde la demande.')}
                        </span>
                      </div>
                    )}
                    {!anonyme ? (
                      <>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <button type="button" onClick={exporterDonnees}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 36, padding: '0 13px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer' }}>
                            <Icon name="download" size={14} stroke={1.9} />
                            
                            {t('Exporter les données du client')}
                          </button>
                          <button type="button" onClick={() => void anonymiser()}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 36, padding: '0 13px', border: '1px solid oklch(0.85 0.06 20)', borderRadius: 'var(--r-ctrl)', background: 'var(--rose-bg)', color: 'var(--rose-fg)', fontSize: '12.5px', fontWeight: 700, cursor: 'pointer' }}>
                            <Icon name="trash" size={14} stroke={1.9} />
                            
                            {t('Anonymiser la fiche')}
                          </button>
                        </div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '10px 12px', borderRadius: 'var(--r-ctrl)', background: 'var(--amber-bg)', fontSize: '11.5px', color: 'var(--amber-fg)', lineHeight: 1.55 }}>
                          <Icon name="alert" size={13} stroke={2} style={{ flexShrink: 0, marginTop: 1 }} />
                          <span><Trans k='<1>Effacement anonymisant, pas fiscal.</1> Les pièces scellées restent intactes (obligation légale) — l’identité est détachée : nom, contacts, naissance, carte et consentements sont effacés, la cagnotte est soldée, l’historique devient orphelin. Le n° client, lui, reste : c’est la référence d’audit de la fiche.' parts={[(x) => <strong>{x}</strong>]} /></span>
                        </div>
                      </>
                    ) : (
                      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '12px 14px', borderRadius: 'var(--r-ctrl)', background: 'var(--ink-100)', fontSize: '12px', color: 'var(--ink-600)', lineHeight: 1.55 }}>
                        <Icon name="lock" size={14} stroke={2} style={{ flexShrink: 0, marginTop: 1 }} />
                        <span><strong>{f.erasedAt ? t('Fiche anonymisée le {v}.', { v: dateEpoch(f.erasedAt) }) : t('Fiche anonymisée.')}</strong>  {t('Identité détachée, consentements coupés. Les pièces fiscales et les mouvements restent scellés — c’est la loi qui l’exige.')}</span>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {cagnotte && detail && <DetailCagnotte detail={detail} onClose={() => setCagnotte(false)} />}
    </div>
  );
}
